import { useCallback, useEffect, useState } from 'react'
import { View, Text, type LayoutChangeEvent } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Animated from 'react-native-reanimated'
import { Check, ShieldCheck } from '@/components/ui/icons'
import { FONTS, COLORS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBookingStore } from '@/lib/stores/booking.store'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { InsurerBadge } from '@/components/insurance/InsurerBadge'
import { CoverageAssistanceCard } from '@/components/insurance/CoverageAssistanceCard'
import { useEntrance } from '@/lib/hooks/use-entrance'

const INCLUDED_COVERAGE_POINTS = [
  'insurance.included_point_auto',
  'insurance.included_point_no_plans',
  'insurance.included_point_assistance',
  'insurance.included_point_benefits',
] as const

/** First-paint estimate only — replaced by the measured height on layout. */
const FOOTER_HEIGHT_ESTIMATE = SPACING.md + 56 + SPACING.md

export default function InsuranceScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const router = useRouter()

  const { insuranceTier, setInsuranceTier } = useBookingStore()

  // The footer is pinned over the scroll, so the scroll has to reserve exactly
  // as much room as the footer occupies. A guessed constant left the last
  // paragraph sliced in half by the footer hairline; measuring it means the
  // reservation survives a taller button, a second line of label, or a device
  // with a different home-indicator inset.
  const [footerHeight, setFooterHeight] = useState(0)
  const handleFooterLayout = useCallback((event: LayoutChangeEvent) => {
    const next = Math.round(event.nativeEvent.layout.height)
    setFooterHeight((prev) => (prev === next ? prev : next))
  }, [])

  useEffect(() => {
    if (insuranceTier !== 'platform') {
      setInsuranceTier('platform')
    }
  }, [insuranceTier, setInsuranceTier])

  const handleContinue = () => {
    setInsuranceTier('platform')
    router.push('/booking/checkout')
  }

  return (
    <ScreenContainer card={false} scroll={false}>
      <View style={{ flex: 1 }}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            paddingBottom: (footerHeight || insets.bottom + FOOTER_HEIGHT_ESTIMATE) + SPACING.lg,
          }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* No paddingHorizontal override: every other stack screen lets
              ScreenHeader use its own SPACING.lg gutter, and the booking flow
              was shifting its back button 8pt left of all of them. */}
          <ScreenHeader title={t('insurance.title')} backTestID="insurance.back-button" />

          <View style={{ paddingHorizontal: SPACING.lg, gap: SPACING.md }}>
            {/* Inline, not the `horizontal` card: that card repeated the
                headline of the included-coverage card below it ("Cobertura
                para viajes") and its "disponible pronto" status, so the screen
                opened with two cards saying the same thing. The inline variant
                keeps the same status line as a subtitle under the header and
                lets the card below own the heading. */}
            <InsurerBadge variant="inline" testID="insurance.provider-badge" />
            <CoverageAssistanceCard testID="insurance.assistance-card" />

            <Animated.View entering={enter(2)} collapsable={false}>
              <View
                testID="insurance.included-coverage-card"
                style={{
                  padding: SPACING.md,
                  borderRadius: RADIUS.lg,
                  borderWidth: 1,
                  borderColor: colors.border,
                  // Same surface as the provider card at the top of the screen.
                  // `surfaceRaised` is brand purple in dark mode, so two of the
                  // three cards on this screen were painted purple and one grey.
                  backgroundColor: colors.surface,
                }}
                accessibilityRole="text"
              >
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}>
                  <View
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: RADIUS.full,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: `${COLORS.coral.DEFAULT}14`,
                      borderWidth: 1,
                      borderColor: `${COLORS.coral.DEFAULT}24`,
                    }}
                  >
                    <ShieldCheck size={24} color={COLORS.coral.DEFAULT} strokeWidth={2.25} />
                  </View>

                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: SPACING.xs,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Text
                        style={{
                          flexShrink: 1,
                          fontSize: 19,
                          lineHeight: 24,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                      >
                        {t('insurance.included_title')}
                      </Text>
                      <View
                        style={{
                          paddingHorizontal: SPACING.sm,
                          paddingVertical: 4,
                          borderRadius: RADIUS.full,
                          backgroundColor: colors.surfaceSubtle,
                          borderWidth: 1,
                          borderColor: colors.border,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 12,
                            lineHeight: 16,
                            fontFamily: FONTS.bodyBold,
                            color: colors.textSecondary,
                          }}
                        >
                          {t('insurance.included_soon_badge')}
                        </Text>
                      </View>
                    </View>
                    <Text
                      style={{
                        marginTop: SPACING.xs,
                        fontSize: 14,
                        lineHeight: 21,
                        fontFamily: FONTS.body,
                        color: colors.textSecondary,
                      }}
                    >
                      {t('insurance.included_desc')}
                    </Text>
                  </View>
                </View>

                <View
                  style={{
                    marginTop: SPACING.md,
                    paddingTop: SPACING.md,
                    borderTopWidth: 1,
                    borderTopColor: colors.border,
                    gap: SPACING.sm,
                  }}
                >
                  {INCLUDED_COVERAGE_POINTS.map((key) => (
                    <View
                      key={key}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: SPACING.sm,
                      }}
                    >
                      <View
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: RADIUS.full,
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginTop: 1,
                          backgroundColor: `${COLORS.coral.DEFAULT}12`,
                        }}
                      >
                        <Check size={13} color={COLORS.coral.DEFAULT} strokeWidth={2.7} />
                      </View>
                      <Text
                        style={{
                          flex: 1,
                          fontSize: 14,
                          lineHeight: 20,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                        }}
                      >
                        {t(key)}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            </Animated.View>

            <Text
              testID="insurance.current-policy-note"
              style={{
                fontSize: 13,
                lineHeight: 19,
                fontFamily: FONTS.body,
                color: colors.textMuted,
              }}
            >
              {t('insurance.current_policy_note')}
            </Text>
          </View>
        </ScrollView>

        <View
          onLayout={handleFooterLayout}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: SPACING.lg,
            paddingTop: SPACING.md,
            paddingBottom: insets.bottom + SPACING.md,
            backgroundColor: colors.background,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}
        >
          <Button
            title={t('common.continue')}
            variant="primary"
            size="lg"
            onPress={handleContinue}
            testID="insurance.continue-button"
            accessibilityRole="button"
            accessibilityLabel={t('common.continue')}
          />
        </View>
      </View>
    </ScreenContainer>
  )
}
